/**
 * B614 leg (b) — the SHOW face's tickets step (555:2 @1440, 555:201 @390),
 * pure half. What feeds what (NOTE 555:249): the badges and the GOOD TO KNOW
 * list are the show's policy facts (B615 — `presentation.policies`); the
 * night chips are the 14-day window ("more via the calendar"), keeping a
 * night the calendar chose past it; a showtime's note is its own age (a
 * per-slot override, else the show's) and the seats a read carried. Every
 * sentence here is drawn only on a SHOW tenant's face — the words are that
 * face's own (the comedy club frames), not a vocabulary pack's.
 */
import type { PolicyFacts } from "@transact/contracts";
import type { Composition } from "./composition.js";
import { quantityOf } from "./composition.js";
import { seatsLeft } from "./browse.js";
import { clockWords } from "@transact/ui";
import type { DayGroup, DisplayOptions } from "../ui/departures.js";
import type { PoolAvailability, ProductSummary, SlotSummary } from "../ui/screens.js";

export const NIGHT_WINDOW_DAYS = 14;
const DAY_MS = 86_400_000;
const COUNT_WORDS = ["Zero", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten"];

/** The photo's badges: "21+", "2-drink minimum", "All sales final" — each only when the fact is there. */
export function policyBadges(p: PolicyFacts | undefined): string[] {
  if (p === undefined) return [];
  return [
    ...(p.minAge !== undefined ? [`${String(p.minAge)}+`] : []),
    ...(p.drinkMinimum !== undefined ? [`${String(p.drinkMinimum)}-drink minimum`] : []),
    ...(p.refundPolicy === "FINAL" ? ["All sales final"] : []),
  ];
}

/** GOOD TO KNOW: doors and seat release in one line, the drink minimum, then each house rule. */
export function goodToKnowLines(p: PolicyFacts | undefined): string[] {
  if (p === undefined) return [];
  const lines: string[] = [];
  const release = (lead: boolean): string | undefined =>
    p.lateSeatingMinutes === undefined
      ? undefined
      : p.lateSeatingMinutes === 0
        ? `${lead ? "Seats" : "seats"} are released at showtime` // vocab-ok: the SHOW face's own words (555:2)
        : `${lead ? "Seats" : "seats"} are released ${String(p.lateSeatingMinutes)} minutes after${lead ? " showtime" : ""}`; // vocab-ok: the SHOW face's own words (555:2)
  if (p.doorsOpenMinutes !== undefined) {
    const tail = release(false);
    lines.push([`Doors open ${String(p.doorsOpenMinutes)} minutes before showtime`, ...(tail !== undefined ? [tail] : [])].join(" · ")); // vocab-ok: the SHOW face's own words (555:2)
  } else {
    const alone = release(true);
    if (alone !== undefined) lines.push(alone);
  }
  if (p.drinkMinimum !== undefined) lines.push(`${COUNT_WORDS[p.drinkMinimum] ?? String(p.drinkMinimum)}-drink minimum per person`);
  return [...lines, ...(p.houseRules ?? [])];
}

/** The night chips: the next 14 days, and a chosen night past them (the calendar's door). */
export function nightWindow(groups: readonly DayGroup[], now: number, selectedKey: string | undefined): DayGroup[] {
  const end = now + NIGHT_WINDOW_DAYS * DAY_MS;
  return groups.filter((g) => {
    const first = g.departures[0]?.start;
    return g.key === selectedKey || (first !== undefined && first >= now && first < end);
  });
}

const fmt = (o: DisplayOptions, f: Intl.DateTimeFormatOptions): Intl.DateTimeFormat =>
  new Intl.DateTimeFormat(o.locale ?? "en-US", { ...f, ...(o.timeZone !== undefined ? { timeZone: o.timeZone } : {}) });
const partsOf = (ms: number, o: DisplayOptions, f: Intl.DateTimeFormatOptions): Record<string, string> =>
  Object.fromEntries(fmt(o, f).formatToParts(ms).map((p) => [p.type, p.value]));

/** "9:45 PM" — 555:2's showtime register; a 24h tenant reads "21:45" (B621). */
export function showtimeWords(ms: number, o: DisplayOptions = {}): string {
  return clockWords(ms, o);
}

/** "Showtime · Friday 2 October" — the showtimes' head. */
export function showtimeHead(ms: number, o: DisplayOptions = {}): string {
  const p = partsOf(ms, o, { weekday: "long", day: "numeric", month: "long" });
  return `Showtime · ${p["weekday"]!} ${p["day"]!} ${p["month"]!}`; // vocab-ok: the SHOW face's own words (555:2)
}

/** A showtime's note: its own age (else the show's) and the seats left or sold out; nothing read, nothing said. */
export function showtimeNote(slot: SlotSummary, showAge: number | undefined, pools: readonly PoolAvailability[] | undefined): string | undefined {
  const age = slot.minAge ?? showAge;
  const left = pools === undefined ? undefined : seatsLeft(pools);
  const parts = [
    ...(age !== undefined ? [`${String(age)}+`] : []),
    ...(left === undefined ? [] : left <= 0 ? ["sold out"] : [`${String(left)} left`]), // vocab-ok: the SHOW face's own chip words (555:2)
  ];
  return parts.length === 0 ? undefined : parts.join(" · ");
}

/** "Fri 2 Oct · 9:45 PM · 2 × VIP" — the night, the time, the counts by each ticket's short name. */
export function picksWhen(card: Pick<ProductSummary, "variants">, comp: Composition, start: number | undefined, o: DisplayOptions = {}): string | undefined {
  const counts = card.variants
    .map((v) => ({ name: v.name.split(" · ")[0]!, q: quantityOf(comp, v.variantId) }))
    .filter((x) => x.q > 0)
    .map((x) => `${String(x.q)} × ${x.name}`);
  if (counts.length === 0 && start === undefined) return undefined;
  const when =
    start === undefined
      ? []
      : (() => {
          const p = partsOf(start, o, { weekday: "short", day: "numeric", month: "short" });
          return [`${p["weekday"]!} ${p["day"]!} ${p["month"]!}`, showtimeWords(start, o)];
        })();
  return [...when, ...(counts.length > 0 ? [counts.join(", ")] : [])].join(" · ");
}

export interface ShowHeadFace {
  readonly key: string;
  readonly title: string;
  readonly when?: string;
  readonly place?: string;
  readonly image?: { readonly url: string; readonly alt?: string };
}

/** "Friday 2 October · 9:45 PM" — the summary's night. */
export function nightWords(ms: number, o: DisplayOptions = {}): string {
  const p = partsOf(ms, o, { weekday: "long", day: "numeric", month: "long" });
  return `${p["weekday"]!} ${p["day"]!} ${p["month"]!} · ${showtimeWords(ms, o)}`;
}

/**
 * B614 (c) — the cart's show heads (555:78): one per show-night the cart
 * holds, in the order they start — the photo, the headliner, the night in
 * words, and the club with that night's age ("18+ tonight" when the night
 * carries its own, else the show's).
 */
export function cartShowHeads(
  lines: readonly { readonly productId: string; readonly slotId?: string; readonly slotStart?: number }[],
  products: readonly ProductSummary[],
  ctx: { readonly clubName?: string; readonly ageOf?: (slotId: string) => number | undefined },
  o: DisplayOptions = {},
): ShowHeadFace[] {
  const seen = new Map<string, { readonly productId: string; readonly slotId?: string; readonly start?: number }>();
  for (const l of lines) {
    const key = `${l.productId}|${l.slotId ?? ""}`;
    if (!seen.has(key)) seen.set(key, { productId: l.productId, ...(l.slotId !== undefined ? { slotId: l.slotId } : {}), ...(l.slotStart !== undefined ? { start: l.slotStart } : {}) });
  }
  return [...seen.entries()]
    .sort((a, b) => (a[1].start ?? Number.POSITIVE_INFINITY) - (b[1].start ?? Number.POSITIVE_INFINITY))
    .flatMap(([key, x]) => {
      const product = products.find((p) => p.productId === x.productId);
      if (product === undefined) return [];
      const nightAge = x.slotId !== undefined ? ctx.ageOf?.(x.slotId) : undefined;
      const age = nightAge !== undefined ? `${String(nightAge)}+ tonight` : product.presentation?.policies?.minAge !== undefined ? `${String(product.presentation.policies.minAge)}+` : undefined;
      const place = [ctx.clubName, age].filter((s): s is string => s !== undefined && s !== "").join(" · ");
      const photo = product.presentation?.photos?.[0];
      return [
        {
          key,
          title: product.presentation?.lineup?.headliner ?? product.name,
          ...(x.start !== undefined ? { when: nightWords(x.start, o) } : {}),
          ...(place !== "" ? { place } : {}),
          ...(photo !== undefined ? { image: { url: photo.url, ...(photo.alt !== undefined ? { alt: photo.alt } : {}) } } : {}),
        },
      ];
    });
}
